<template>
  <div class="legal-doc-wrapper">
    <!-- ── Control Bar (no-print) ── -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Terms Acknowledgement</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportItems.length }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print</span>
        </button>
        <button class="btn-modern btn-close-btn" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
      <p class="mt-3 font-weight-bold">Loading acknowledgement data...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <!-- Report Pages -->
    <div v-else>
      <div
        v-if="reportItems.length === 0"
        class="text-center py-5 text-muted no-print"
      >
        <feather-icon icon="AlertCircleIcon" size="48" class="mb-3" />
        <p>No acknowledgement data available.</p>
      </div>

      <!-- One page per student -->
      <div
        v-for="(item, index) in reportItems"
        :key="item.id || index"
        class="legal-page"
        :class="{ 'page-break': index > 0 }"
      >
        <!-- ════════════════════════════════
             LETTERHEAD
             ════════════════════════════════ -->
        <div class="lh-wrap">
          <!-- Decorative double-rule top border -->
          <div class="lh-border-top">
            <div class="lh-rule-thick"></div>
            <div class="lh-rule-thin"></div>
          </div>

          <div class="lh-body">
            <!-- Logo -->
            <div class="lh-logo-col" v-if="schoolLogo">
              <img :src="schoolLogo" alt="Institution Logo" class="lh-logo" />
            </div>

            <!-- Centre text -->
            <div class="lh-center-col">
              <div class="lh-institution-name">{{ campusName }}</div>
              <div class="lh-institution-sub" v-if="campusBranch">
                {{ campusBranch }}
              </div>
              <div class="lh-doc-type">
                Terms &amp; Conditions Acknowledgement
              </div>
              <div class="lh-ref-row">
                <span class="lh-ref-item"
                  >Ref No.: <strong>{{ item.grNo || "N/A" }}</strong></span
                >
                <span class="lh-ref-sep">|</span>
                <span class="lh-ref-item"
                  >Dated: <strong>{{ formatDate(item.date) }}</strong></span
                >
                <span class="lh-ref-sep">|</span>
                <span class="lh-ref-item"
                  >Document:
                  <strong>{{
                    (item.terms && item.terms.title) || "Terms & Conditions"
                  }}</strong></span
                >
              </div>
            </div>

            <!-- Seal / stamp area -->
            <div class="lh-seal-col">
              <div class="lh-seal">
                <div class="lh-seal-inner">
                  <div class="lh-seal-text-top">OFFICIAL</div>
                  <div class="lh-seal-star">★</div>
                  <div class="lh-seal-text-bot">DOCUMENT</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Decorative double-rule bottom border -->
          <div class="lh-border-bot">
            <div class="lh-rule-thin"></div>
            <div class="lh-rule-thick"></div>
          </div>
        </div>

        <!-- ════════════════════════════════
             PREAMBLE — parties declaration
             ════════════════════════════════ -->
        <div class="preamble-block">
          <p class="preamble-text">
            This Agreement of Acknowledgement (<strong>"Agreement"</strong>) is
            entered into on
            <strong>{{ formatDate(item.date) }}</strong
            >, by and between
            <strong>{{ campusName }}</strong>
            <span v-if="campusBranch">
              — <em>{{ campusBranch }}</em></span
            >
            (hereinafter referred to as the <strong>"Institution"</strong>), and
            <strong>{{ item.studentName || "Student" }}</strong>
            (hereinafter referred to as the <strong>"Student"</strong>).
          </p>
          <p class="preamble-text">
            The Student hereby acknowledges having read, understood, and agreed
            to be bound by the Terms &amp; Conditions set forth in the document
            titled
            <strong
              >"{{
                (item.terms && item.terms.title) || "Terms & Conditions"
              }}"</strong
            >
            as issued by the Institution. By signing below, the Student and
            Parent/Guardian confirm full acceptance of the following provisions
            without reservation.
          </p>
        </div>

        <!-- ════════════════════════════════
             TERMS DOCUMENT BODY
             ════════════════════════════════ -->
        <div class="terms-body">
          <!-- Document title banner -->
          <div class="terms-title-banner">
            <div class="terms-title-rule left"></div>
            <h2 class="terms-title-text">
              {{ (item.terms && item.terms.title) || "Terms &amp; Conditions" }}
            </h2>
            <div class="terms-title-rule right"></div>
          </div>

          <!-- Quill content -->
          <div
            class="ql-content-wrapper"
            v-if="item.terms && item.terms.content"
            v-html="cleanHtml(item.terms.content)"
          ></div>
          <div v-else class="no-content-note">
            <p>No terms content available for this document.</p>
          </div>
        </div>

        <!-- ════════════════════════════════
             ACKNOWLEDGEMENT DECLARATION
             ════════════════════════════════ -->
        <div class="declaration-block">
          <div class="declaration-rule"></div>
          <p class="declaration-text">
            <strong>DECLARATION:</strong> I,
            <strong>{{ item.father_name || "_______________" }}</strong
            >, hereby declare that I have carefully read and fully understood
            all the terms and conditions stated above. I voluntarily agree to
            abide by these terms and acknowledge that violation of any clause
            may result in disciplinary action as determined by the Institution.
          </p>
          <div class="declaration-rule"></div>
        </div>

        <!-- ════════════════════════════════
             SIGNATURE BLOCK
             ════════════════════════════════ -->
        <div class="sig-section">
          <div class="sig-cols">
            <!-- Student / Signee -->
            <div class="sig-col">
              <div class="sig-party-label">STUDENT / SIGNEE</div>
              <div class="sig-name-prefill">
                {{ item.studentName || "________________________________" }}
              </div>
              <div class="sig-area"></div>
              <div class="sig-line-label">Signature</div>
              <div class="sig-meta-row">
                <span class="sig-meta-key">Name:</span>
                <span class="sig-meta-val sig-meta-line">{{
                  item.studentName || ""
                }}</span>
              </div>
              <div class="sig-meta-row">
                <span class="sig-meta-key">GR No.:</span>
                <span class="sig-meta-val sig-meta-line">{{
                  item.grNo || ""
                }}</span>
              </div>
            </div>

            <!-- Divider -->
            <div class="sig-divider"></div>

            <!-- Parent / Guardian -->
            <div class="sig-col">
              <div class="sig-party-label">PARENT / GUARDIAN</div>
              <div class="sig-name-prefill">
                {{ item.father_name || "&nbsp;" }}
              </div>
              <div class="sig-area"></div>
              <div class="sig-line-label">Signature</div>
              <div class="sig-meta-row">
                <span class="sig-meta-key">Name:</span>
                <span class="sig-meta-val sig-meta-line">{{
                  item.father_name || ""
                }}</span>
              </div>
            </div>

            <!-- Divider -->
            <div class="sig-divider"></div>

            <!-- Agreement Details -->
            <div class="sig-col agreement-details-col">
              <div class="sig-party-label">AGREEMENT DETAILS</div>
              <div class="sig-status-wrap">
                <div class="sig-status-item">
                  <span class="sig-status-key">Status:</span>
                  <span class="sig-status-val">Agreed</span>
                </div>

                <div class="sig-status-item">
                  <span class="sig-status-key">Timestamp:</span>
                  <span class="sig-status-val">{{
                    item.date ? new Date(item.date).toLocaleString() : "N/A"
                  }}</span>
                </div>
              </div>
              <div class="sig-agreement-note">
                Electronically acknowledged and verified via MySkool Mobile
                Application.
              </div>
            </div>
          </div>
        </div>

        <!-- ════════════════════════════════
             PAGE FOOTER
             ════════════════════════════════ -->
        <div class="legal-page-footer">
          <div class="footer-rule-thick"></div>
          <div class="footer-rule-thin"></div>
          <div class="footer-content">
            <span
              >{{ campusName
              }}<span v-if="campusBranch"> — {{ campusBranch }}</span></span
            >
            <span class="footer-sep">|</span>
            <span
              >Document:
              {{
                (item.terms && item.terms.title) || "Terms & Conditions"
              }}</span
            >
            <span class="footer-sep">|</span>
            <span>Generated: {{ currentDateTime }}</span>
            <span class="footer-sep">|</span>
            <span>Powered by MySkool.app</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "TermsAcknowledgePrint",
  components: { BButton, BSpinner, BBadge },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      schoolLogo: null,
      campusName: "",
      campusBranch: "",
      db: "",
      cID: 0,
      classID: 0,
      studentFilter: "",
    };
  },
  computed: {
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const q = this.$route.query;
      this.db = q.db;
      this.cID = q.cID || q.cId || 0;
      this.classID = q.classID || 0;
      this.studentFilter = q.sID || q.search || "";
      this.campusName =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.branch) ||
        "My Skool";
      this.schoolLogo =
        (this.$store.state.currentBranch &&
          this.$store.state.currentBranch.logo) ||
        null;
      if (!this.db) {
        this.error = "Invalid parameters. Please close and try again.";
        return;
      }
      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const res = await axios.get("https://apiv26.myskool.app/api/campuses", {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });
        if (
          res.data &&
          res.data.status === "success" &&
          res.data.data &&
          res.data.data.length > 0
        ) {
          const match = res.data.data.find((c) => c.id == this.cID);
          const info = match || res.data.data[0];
          this.campusName = info.name;
          this.campusBranch = this.cID == 0 ? "All Campuses" : info.branch;
          this.schoolLogo = info.logo;
        }
      } catch (e) {
        console.error("Campus info error:", e);
      }
    },
    async loadReportData() {
      try {
        this.loading = true;
        this.error = null;
        let baseUrl = this.$store.state.domain;
        if (baseUrl.endsWith("/")) baseUrl = baseUrl.slice(0, -1);
        const url = `${baseUrl}/Terms/GetAcceptedTermsDetails`;
        const params = { db: this.db, campusID: this.cID, type: "Student" };
        if (this.classID && this.classID != 0) params.classID = this.classID;
        if (this.studentFilter) params.sID = this.studentFilter;
        const res = await axios.get(url, {
          params,
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });
        if (res.data && res.data.status === "success") {
          this.reportItems = res.data.data || [];
        } else {
          this.error =
            res.data?.message || "Failed to load acknowledgement data.";
        }
      } catch (err) {
        console.error("Terms acknowledge error:", err);
        this.error = "Network error or server unavailable.";
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "________________";
      try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      } catch {
        return dateStr;
      }
    },
    cleanHtml(html) {
      if (!html) return "";
      return html
        .replace(/(&nbsp;|\s){2,}/g, " ")
        .replace(/<span style="color: black;">/g, "<span>")
        .replace(/ql-align-justify/g, "ql-align-left"); // Prevent stretched spacing in numbers
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss">
/* ─────────────────────────────────────────────
   GOOGLE FONT
   ───────────────────────────────────────────── */
@import url("https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,600;0,700;0,800;1,400;1,600&family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&display=swap");

/* ─────────────────────────────────────────────
   ROOT WRAPPER
   ───────────────────────────────────────────── */
.legal-doc-wrapper {
  background: #e8e4dc;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "EB Garamond", "Times New Roman", serif;
  color: #1a1209;
}

/* ─────────────────────────────────────────────
   CONTROL BAR
   ───────────────────────────────────────────── */
.control-bar {
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(12px);
  padding: 1rem 2rem;
  margin-bottom: 2rem;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid #d5c9b0;
  h4 {
    color: #2c2008;
    font-weight: 700;
    font-size: 1.15rem;
    font-family: "EB Garamond", serif;
  }
}
.action-buttons {
  display: flex;
  gap: 10px;
}
.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 9px 22px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  span {
    margin-left: 4px;
  }
  &:active {
    transform: scale(0.96);
  }
}
.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;
  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}
.btn-close-btn {
  background: white;
  color: #555;
  border: 1px solid #ccc;
  &:hover {
    color: #c0392b;
    border-color: #e74c3c;
    transform: translateY(-1px);
  }
}

/* ─────────────────────────────────────────────
   LEGAL PAGE
   ───────────────────────────────────────────── */
.legal-page {
  background: #fffdf8;
  width: 98%;
  max-width: 900px;
  margin: 0 auto 3rem;
  padding: 2.5rem 3.5rem;
  box-shadow: 0 0 0 1px #c9b98a, 0 0 0 4px #fffdf8, 0 0 0 6px #b8a570,
    0 6px 40px rgba(0, 0, 0, 0.22);
  box-sizing: border-box;
  position: relative;

  /* Watermark */
  &::before {
    content: "ACKNOWLEDGED";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-45deg);
    font-size: 5rem;
    font-weight: 900;
    color: rgba(180, 160, 100, 0.07);
    letter-spacing: 0.3rem;
    white-space: nowrap;
    pointer-events: none;
    z-index: 0;
    font-family: "EB Garamond", serif;
  }
}

/* ─────────────────────────────────────────────
   LETTERHEAD
   ───────────────────────────────────────────── */
.lh-wrap {
  position: relative;
  z-index: 1;
  margin-bottom: 1.2rem;
}
.lh-border-top,
.lh-border-bot {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.lh-border-top {
  margin-bottom: 18px;
}
.lh-border-bot {
  margin-top: 18px;
}
.lh-rule-thick {
  height: 3px;
  background: #1a3a5c;
}
.lh-rule-thin {
  height: 1px;
  background: #8a7040;
}

.lh-body {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 0.5rem 0;
}

.lh-logo-col {
  flex-shrink: 0;
}
.lh-logo {
  height: 88px;
  width: auto;
  object-fit: contain;
}

.lh-center-col {
  flex: 1;
  text-align: center;
}
.lh-institution-name {
  font-size: 1.75rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #1a3a5c;
  line-height: 1.2;
  font-family: "Cormorant Garamond", "EB Garamond", serif;
}
.lh-institution-sub {
  font-size: 0.88rem;
  color: #6b5c3e;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-top: 2px;
}
.lh-doc-type {
  display: inline-block;
  margin-top: 10px;
  padding: 3px 20px;
  border-top: 1px solid #8a7040;
  border-bottom: 1px solid #8a7040;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #8a7040;
}
.lh-ref-row {
  margin-top: 10px;
  font-size: 0.8rem;
  color: #5a4a2e;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0 8px;
}
.lh-ref-item {
}
.lh-ref-sep {
  color: #b8a570;
}

.lh-seal-col {
  flex-shrink: 0;
}
.lh-seal {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  border: 3px solid #1a3a5c;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-shadow: inset 0 0 0 4px #fffdf8, inset 0 0 0 6px #1a3a5c;
}
.lh-seal-inner {
  text-align: center;
  line-height: 1.1;
}
.lh-seal-text-top,
.lh-seal-text-bot {
  font-size: 0.5rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1a3a5c;
}
.lh-seal-star {
  font-size: 1.2rem;
  color: #8a7040;
  line-height: 1.2;
}

/* ─────────────────────────────────────────────
   PREAMBLE
   ───────────────────────────────────────────── */
.preamble-block {
  position: relative;
  z-index: 1;
  border-left: 4px solid #1a3a5c;
  padding: 1rem 1.5rem;
  margin-bottom: 1.2rem;
  background: rgba(26, 58, 92, 0.04);
}
.preamble-text {
  font-size: 0.97rem;
  line-height: 1.55;
  color: #2c1f0e;
  text-align: justify;
  margin: 0 0 0.4rem 0;
  &:last-child {
    margin-bottom: 0;
  }
}

/* ─────────────────────────────────────────────
   TERMS BODY
   ───────────────────────────────────────────── */
.terms-body {
  position: relative;
  z-index: 1;
  margin-bottom: 1.5rem;
}

.terms-title-banner {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.8rem;
}
.terms-title-rule {
  flex: 1;
  height: 0;
  border-top: 2px solid #1a3a5c;
  &.left::before {
    content: none;
  }
}
.terms-title-text {
  font-family: "Cormorant Garamond", "EB Garamond", serif;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #1a3a5c;
  white-space: nowrap;
  margin: 0;
}

/* ─────────────────────────────────────────────
   QUILL CONTENT
   ───────────────────────────────────────────── */
.ql-content-wrapper {
  font-family: "EB Garamond", "Times New Roman", serif;
  font-size: 1rem;
  line-height: 1.6;
  color: #1a1209;
  text-align: justify;

  .ql-align-center {
    text-align: center !important;
  }
  .ql-align-right {
    text-align: right !important;
  }
  .ql-align-justify {
    text-align: justify !important;
  }
  .ql-align-left {
    text-align: left !important;
  }

  h1 {
    font-size: 1.4rem;
    font-weight: 800;
    color: #1a3a5c;
    margin: 0.8rem 0 0.4rem;
    font-family: "Cormorant Garamond", serif;
  }
  h2 {
    font-size: 1.15rem;
    font-weight: 700;
    color: #1a3a5c;
    margin: 0.9rem 0 0.35rem;
  }
  h3 {
    font-size: 1rem;
    font-weight: 700;
    color: #2c3e50;
    margin: 0.7rem 0 0.3rem;
  }

  /* Override Quill specific alignment if it breaks number spacing */
  .ql-align-justify {
    text-align: left !important;
  }
  h2.ql-align-justify,
  h1.ql-align-justify {
    text-align: center !important;
  }

  p {
    margin: 0.25rem 0;
    line-height: 1.6;
  }

  strong,
  b {
    font-weight: 700;
    color: #0e1f30;
  }
  em,
  i {
    font-style: italic;
  }
  u {
    text-decoration: underline;
  }
  s {
    text-decoration: line-through;
  }

  a {
    color: #1a3a5c;
    text-decoration: underline;
  }

  ul,
  ol {
    padding-left: 1.75rem;
    margin: 0.4rem 0;
    li {
      margin: 0.15rem 0;
      line-height: 1.55;
    }
  }

  blockquote {
    border-left: 3px solid #8a7040;
    padding: 0.5rem 1rem;
    margin: 0.75rem 0;
    color: #4a3a20;
    background: rgba(138, 112, 64, 0.06);
    font-style: italic;
  }

  pre,
  code {
    font-family: monospace;
    background: #f1f0eb;
    border-radius: 3px;
    padding: 2px 5px;
    font-size: 0.88rem;
  }

  sup {
    vertical-align: super;
    font-size: 0.75em;
  }
  sub {
    vertical-align: sub;
    font-size: 0.75em;
  }
  br {
    display: block;
    margin: 2px 0;
  }
}

.no-content-note {
  text-align: center;
  color: #999;
  padding: 2rem;
  font-style: italic;
}

/* ─────────────────────────────────────────────
   DECLARATION
   ───────────────────────────────────────────── */
.declaration-block {
  position: relative;
  z-index: 1;
  margin: 1.2rem 0;
}
.declaration-rule {
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    #8a7040 30%,
    #8a7040 70%,
    transparent
  );
  margin: 10px 0;
}
.declaration-text {
  font-size: 0.95rem;
  line-height: 1.6;
  text-align: justify;
  color: #2c1f0e;
  padding: 0.6rem 0;
  margin: 0;
}

/* ─────────────────────────────────────────────
   SIGNATURE SECTION
   ───────────────────────────────────────────── */
.sig-section {
  position: relative;
  z-index: 1;
  margin-top: 1.2rem;
}

.sig-cols {
  display: flex;
  gap: 0;
  border: 1.5px solid #1a3a5c;
}
.sig-col {
  flex: 1;
  padding: 0.8rem 1.2rem;
  position: relative;
}
.sig-divider {
  width: 1.5px;
  background: #1a3a5c;
  flex-shrink: 0;
}

.sig-party-label {
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #1a3a5c;
  margin-bottom: 0.5rem;
  padding-bottom: 6px;
  border-bottom: 1px solid #b8a570;
}

.sig-name-prefill {
  font-size: 1rem;
  font-weight: 700;
  color: #1a1209;
  min-height: 1.4rem;
  font-family: "Cormorant Garamond", serif;
  font-style: italic;
  margin-bottom: 0.25rem;
}

.sig-area {
  height: 42px;
  border-bottom: 1.5px solid #1a1209;
  margin: 0.4rem 0;
}

.sig-line-label {
  font-size: 0.65rem;
  color: #7a6a50;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
}

.sig-meta-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 6px;
}
.sig-meta-key {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4a3a20;
  white-space: nowrap;
  flex-shrink: 0;
}
.sig-meta-val {
  font-size: 0.85rem;
  color: #1a1209;
  flex: 1;
}
.sig-meta-line {
  border-bottom: 1px solid #b8a570;
  min-width: 60px;
  display: inline-block;
}

.agreement-details-col {
  background: rgba(26, 58, 92, 0.02);
}
.sig-status-wrap {
  margin-top: 0.5rem;
}
.sig-status-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  border-bottom: 1px dashed #d5c9b0;
  padding-bottom: 2px;
}
.sig-status-key {
  font-size: 0.65rem;
  font-weight: 700;
  color: #6b5c3e;
  text-transform: uppercase;
}
.sig-status-val {
  font-size: 0.75rem;
  font-weight: 700;
  color: #1a3a5c;
}
.sig-agreement-note {
  margin-top: 0.8rem;
  font-size: 0.7rem;
  font-style: italic;
  color: #8a7040;
  line-height: 1.3;
  text-align: center;
}

/* ─────────────────────────────────────────────
   PAGE FOOTER
   ───────────────────────────────────────────── */
.legal-page-footer {
  position: relative;
  z-index: 1;
  margin-top: 1.5rem;
}
.footer-rule-thick {
  height: 2.5px;
  background: #1a3a5c;
}
.footer-rule-thin {
  height: 1px;
  background: #8a7040;
  margin-top: 3px;
}
.footer-content {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0 8px;
  padding-top: 8px;
  font-size: 0.72rem;
  color: #7a6a50;
  letter-spacing: 0.04em;
}
.footer-sep {
  color: #b8a570;
}

/* ─────────────────────────────────────────────
   PRINT STYLES
   ───────────────────────────────────────────── */
@page {
  size: auto;
  margin: 0mm;
}

@media print {
  .no-print {
    display: none !important;
  }

  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }

  /* Protect content from edge cutting while hiding browser headers */
  .legal-page {
    margin: 0 !important;
    padding: 0.5cm 0.4cm 1.2cm !important;
    box-shadow: none !important;
    border: none !important;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
    page-break-after: always;

    &:last-child {
      page-break-after: auto !important;
    }
  }

  .legal-page-footer {
    display: none !important;
  }

  .lh-institution-name {
    color: #000 !important;
  }
  .lh-rule-thick,
  .footer-rule-thick {
    background: #000 !important;
  }
  .lh-rule-thin,
  .footer-rule-thin {
    background: #555 !important;
  }
  .lh-seal {
    border-color: #000 !important;
    box-shadow: inset 0 0 0 4px #fff, inset 0 0 0 6px #000 !important;
  }
  .lh-seal-text-top,
  .lh-seal-text-bot {
    color: #000 !important;
  }

  .preamble-block {
    background: #fafafa !important;
    border-left-color: #000 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .terms-title-text {
    color: #000 !important;
  }
  .terms-title-rule {
    border-top-color: #000 !important;
  }

  .ql-content-wrapper {
    font-size: 9.5pt !important;
    color: #000 !important;
    h1 {
      font-size: 13pt !important;
      color: #000 !important;
    }
    h2 {
      font-size: 11pt !important;
      color: #000 !important;
    }
    h3 {
      font-size: 10pt !important;
      color: #000 !important;
    }
    p,
    li {
      color: #000 !important;
    }
    strong,
    b {
      color: #000 !important;
    }
  }

  .declaration-rule {
    background: #000 !important;
  }

  .sig-cols {
    border-color: #000 !important;
  }
  .sig-divider {
    background: #000 !important;
  }
  .sig-party-label {
    color: #000 !important;
    border-bottom-color: #000 !important;
  }
  .sig-area {
    border-bottom-color: #000 !important;
  }
  .sig-meta-line {
    border-bottom-color: #000 !important;
  }

  .legal-page-footer .footer-content {
    color: #555 !important;
  }
}
</style>
